import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Footer from '../components/layout/Footer';
import { Check, Star, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// NeuralNext Logo
const NEURALNEXT_LOGO = "https://customer-assets.emergentagent.com/job_datasignz-web/artifacts/843aq8gt_NeuralNext-Logo.png";

// Raster overlay
const RASTER_URL = "https://customer-assets.emergentagent.com/job_5d2b1704-c912-40b5-94d1-19a432268dc7/artifacts/k5lfkxbh_Raster.png";

// Header images for Web Services page
const headerImages = [
  { id: 1, media: 'https://customer-assets.emergentagent.com/job_datasignz-web/artifacts/cxg9v9c2_1.png' },
  { id: 2, media: 'https://customer-assets.emergentagent.com/job_datasignz-web/artifacts/0v7eut2z_2.png' },
  { id: 3, media: 'https://customer-assets.emergentagent.com/job_datasignz-web/artifacts/77rk0rgy_3.png' },
  { id: 4, media: 'https://customer-assets.emergentagent.com/job_datasignz-web/artifacts/xe20tuip_4.png' }
];

// Slider images for Web Services with links
const sliderImages = [
  {
    url: 'https://customer-assets.emergentagent.com/job_design-portfolio-401/artifacts/6odtq4gl_Datasignz.jpg',
    link: 'https://datasignz.nl',
    label: 'datasignz.nl'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_design-portfolio-401/artifacts/quw9hdie_94greentv.jpg',
    link: 'https://94greentv.com',
    label: '94greentv.com'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_design-portfolio-401/artifacts/ewd6o2n5_japanmotors.jpg',
    link: 'https://japanmotors.sr',
    label: 'japanmotors.sr'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_datasignz-web/artifacts/pcfieahq_Olive-thumbnail.png',
    link: 'https://olivegroupsr.com',
    label: 'olivegroupsr.com'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_datasignz-web/artifacts/jdzqsncx_Brunchbrigade.png',
    link: 'https://thebrunchbrigade.com',
    label: 'thebrunchbrigade.com'
  }
];

// Menu items
const menuItems = [
  { nameKey: 'home', path: '/' },
  { nameKey: 'videoProduction', path: '/video-production' },
  { nameKey: 'postProduction', path: '/post-production' },
  { nameKey: 'onAirIdentity', path: '/on-air-identity' },
  { nameKey: 'marketingBranding', path: '/marketing-branding' },
  { nameKey: 'webServices', path: 'https://www.neuralnext.online/', external: true },
  { nameKey: 'contact', path: '/contact' }
];

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const WebServicesPage = () => {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const langMenuRef = useRef(null);
  
  // Header image slider state
  const shuffledHeaderImages = useMemo(() => shuffleArray(headerImages), []);
  const [currentHeaderSlide, setCurrentHeaderSlide] = useState(0);
  const [isHeaderTransitioning, setIsHeaderTransitioning] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  
  // Content slider state
  const [currentImageSlide, setCurrentImageSlide] = useState(0);
  const [shuffledImages] = useState(() => shuffleArray(sliderImages));

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    };
    
    if (langMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [langMenuOpen]);

  // Header slider auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeaderSlide((prev) => (prev + 1) % shuffledHeaderImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [shuffledHeaderImages.length]);

  // Content slider auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageSlide((prev) => (prev + 1) % shuffledImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [shuffledImages.length]);

  const nextHeaderSlide = () => {
    if (isHeaderTransitioning) return;
    setIsHeaderTransitioning(true);
    setCurrentHeaderSlide((prev) => (prev + 1) % shuffledHeaderImages.length);
    setTimeout(() => setIsHeaderTransitioning(false), 500);
  };

  const prevHeaderSlide = () => {
    if (isHeaderTransitioning) return;
    setIsHeaderTransitioning(true);
    setCurrentHeaderSlide((prev) => (prev - 1 + shuffledHeaderImages.length) % shuffledHeaderImages.length);
    setTimeout(() => setIsHeaderTransitioning(false), 500);
  };

  const nextImageSlide = () => {
    setCurrentImageSlide((prev) => (prev + 1) % shuffledImages.length);
  };

  const prevImageSlide = () => {
    setCurrentImageSlide((prev) => (prev - 1 + shuffledImages.length) % shuffledImages.length);
  };

  return (
    <div className="min-h-screen">
      {/* Custom Header with Image Slider and NeuralNext Logo */}
      <header className="relative w-full min-h-[60vh] md:h-screen overflow-hidden bg-[#0b004c]">
        {/* Background Images */}
        {shuffledHeaderImages.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-500 flex items-center justify-center ${
              index === currentHeaderSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.media}
              alt=""
              className="w-full h-full object-contain md:object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
          </div>
        ))}

        {/* Raster Overlay */}
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            backgroundImage: `url(${RASTER_URL})`,
            backgroundRepeat: 'repeat',
            opacity: 0.5
          }}
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/30 z-10" />

        {/* NeuralNext Logo Centered */}
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <img
            src={NEURALNEXT_LOGO}
            alt="NeuralNext"
            className="w-48 md:w-64 lg:w-80 h-auto object-contain drop-shadow-2xl"
          />
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevHeaderSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2 text-white/70 hover:text-white transition-colors"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-8 h-8 md:w-10 md:h-10" />
        </button>
        <button
          onClick={nextHeaderSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2 text-white/70 hover:text-white transition-colors"
          aria-label="Next slide"
        >
          <ChevronRight className="w-8 h-8 md:w-10 md:h-10" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {shuffledHeaderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentHeaderSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentHeaderSlide ? 'bg-white w-6' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </header>

      {/* Navigation Menu Bar - matching standard Header style */}
      <nav className="bg-[#0b004c] sticky top-0 z-50">
        <div className="py-4">
          <ul className="flex flex-wrap justify-center items-center gap-x-2 md:gap-x-4 px-4">
            {menuItems.map((item, index) => (
              <React.Fragment key={item.path}>
                <li>
                  {item.external ? (
                    <a
                      href={item.path}
                      className="font-montserrat-bold text-xs md:text-sm tracking-wider transition-colors duration-300 text-gray-500 hover:text-[#1a1a8a]"
                    >
                      {t(item.nameKey)}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={`font-montserrat-bold text-xs md:text-sm tracking-wider transition-colors duration-300 ${
                        location.pathname === item.path
                          ? 'text-white'
                          : 'text-white/80 hover:text-[#6a6a6a]'
                      }`}
                    >
                      {t(item.nameKey)}
                    </Link>
                  )}
                </li>
                {index < menuItems.length - 1 && (
                  <li className="text-white/60 hidden md:block">|</li>
                )}
              </React.Fragment>
            ))}
            {/* Language Switcher in Menu Bar */}
            <li className="text-white/60 hidden md:block">|</li>
            <li className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1 text-white/80 hover:text-white transition-colors font-montserrat-bold text-xs md:text-sm"
                data-testid="language-switcher-btn"
              >
                <Globe className="w-4 h-4" />
                <span>{languages.find(l => l.code === language)?.flag}</span>
              </button>
              
              {langMenuOpen && (
                <div 
                  className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-[#0b004c] border border-white/20 rounded-lg shadow-lg overflow-hidden min-w-[150px]"
                  style={{ zIndex: 9999 }}
                  data-testid="language-dropdown"
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setLangMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm font-montserrat flex items-center gap-2 transition-colors ${
                        language === lang.code
                          ? 'bg-white/20 text-white'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                      data-testid={`lang-option-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </li>
          </ul>
        </div>
      </nav>
      
      {/* Page Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          {/* Video Player */}
          <div className="flex justify-center mb-8">
            <div className="w-full max-w-3xl aspect-video rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.youtube.com/embed/p1kfGMWhPX0?autoplay=1&mute=1&loop=1&playlist=p1kfGMWhPX0&rel=0&modestbranding=1"
                title="NeuralNext Web Services"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          
          <div className="text-center mb-12">
            <h1 className="font-montserrat-bold text-3xl md:text-4xl text-[#0b004c] mb-8">
              {t('webServicesTitle')}
            </h1>
            
            {/* Two Column Text */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto text-left">
              <div>
                <p className="font-montserrat text-[#6a6a6a] mb-4">
                  {t('webServicesDesc1')}
                </p>
                <p className="font-montserrat text-[#6a6a6a]">
                  {t('webServicesDesc2')}
                </p>
              </div>
              <div>
                <p className="font-montserrat text-[#6a6a6a] mb-4">
                  {t('webServicesDesc3')}
                </p>
                <p className="font-montserrat text-[#6a6a6a] mb-4">
                  {t('webServicesDesc4')}
                </p>
                <p className="font-montserrat text-[#6a6a6a] font-semibold">
                  {t('webServicesCTA')}
                </p>
              </div>
            </div>
          </div>

          {/* Portfolio Header */}
          <h2 className="font-montserrat-bold text-2xl md:text-3xl text-[#0b004c] text-center mb-8">
            {t('ourPortfolio')}
          </h2>

          {/* Image Slider */}
          <div className="relative w-full h-[40vh] md:h-[60vh] overflow-hidden rounded-lg mb-16">
            {/* Background Images */}
            {shuffledImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  index === currentImageSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={image.url}
                  alt={image.label}
                  className="w-full h-full object-cover"
                />
                {/* Link Label */}
                <a
                  href={image.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-16 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-6 py-2 rounded-full font-montserrat-bold text-sm md:text-base hover:bg-[#0b004c] transition-colors duration-300"
                >
                  {image.label}
                </a>
              </div>
            ))}

            {/* Navigation Arrows */}
            <button
              onClick={prevImageSlide}
              className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-white bg-black/30 flex items-center justify-center transition-all duration-300 hover:bg-white/30"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </button>

            <button
              onClick={nextImageSlide}
              className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-white bg-black/30 flex items-center justify-center transition-all duration-300 hover:bg-white/30"
              aria-label="Next image"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </button>

            {/* Slide Indicators */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
              {shuffledImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentImageSlide ? 'bg-white w-6' : 'bg-white/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { titleKey: 'landingPage', price: 'USD 250', deliveryKey: 'delivery', deliveryDays: 2, features: ['basicDesign', 'responsiveLayout', 'contactForm', 'socialMediaIntegration'] },
              { titleKey: 'websiteBudget', price: 'USD 500', deliveryKey: 'delivery', deliveryDays: 3, features: ['photoGallery', 'fourPages', 'bookingsForm', 'socialMediaIntegration'] },
              { titleKey: 'websiteStandard', price: 'USD 1,250', deliveryKey: 'delivery', deliveryDays: 5, features: ['customDesign', 'maxTenPages', 'photoGalleryVideo', 'bookingQuoteForm'], popular: true },
              { titleKey: 'websiteElite', price: 'USD 1,750', deliveryKey: 'delivery', deliveryDays: 7, features: ['customDesign', 'maxTwentyFivePages', 'photoGalleryVideo', 'bookingQuoteForm'] }
            ].map((pkg, index) => (
              <div
                key={index}
                className={`rounded-lg p-6 ${
                  pkg.popular
                    ? 'bg-[#0b004c] text-white ring-2 ring-[#0b004c] ring-offset-2'
                    : 'bg-white border border-gray-200'
                } hover:shadow-xl transition-all duration-300`}
              >
                {pkg.popular && (
                  <div className="flex items-center gap-1 mb-4">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-xs font-montserrat-bold">{t('mostPopular')}</span>
                  </div>
                )}
                <h3 className={`font-montserrat-bold text-xl mb-2 ${
                  pkg.popular ? 'text-white' : 'text-[#0b004c]'
                }`}>
                  {t(pkg.titleKey)}
                </h3>
                <div className="mb-4">
                  <span className={`font-montserrat-bold text-3xl ${
                    pkg.popular ? 'text-white' : 'text-[#0b004c]'
                  }`}>
                    {pkg.price}
                  </span>
                </div>
                <p className={`font-montserrat text-sm mb-6 ${
                  pkg.popular ? 'text-white/80' : 'text-[#6a6a6a]'
                }`}>
                  {t('delivery')}: {pkg.deliveryDays} {t('days')}
                </p>
                <ul className="space-y-3">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        pkg.popular ? 'text-white' : 'text-[#0b004c]'
                      }`} />
                      <span className={`font-montserrat text-sm ${
                        pkg.popular ? 'text-white/90' : 'text-[#6a6a6a]'
                      }`}>
                        {t(feature)}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href="/contact"
                  className={`block text-center mt-6 py-3 rounded font-montserrat-bold text-sm transition-colors duration-300 ${
                    pkg.popular
                      ? 'bg-white text-[#0b004c] hover:bg-[#6a6a6a] hover:text-white'
                      : 'bg-[#0b004c] text-white hover:bg-[#6a6a6a]'
                  }`}
                >
                  {t('getStarted')}
                </a>
              </div>
            ))}
          </div>

          {/* Extra Options */}
          <div className="bg-gray-50 rounded-lg p-8">
            <h2 className="font-montserrat-bold text-2xl text-[#0b004c] text-center mb-8">
              {t('additionalOptions')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { nameKey: 'extraVideoHeader', priceKey: 'extraVideoHeaderPrice' },
                { nameKey: 'extraSEO', priceKey: 'extraSEOPrice' },
                { nameKey: 'extraWhatsApp', priceKey: 'extraWhatsAppPrice' },
                { nameKey: 'extraChatBot', priceKey: 'extraChatBotPrice' },
                { nameKey: 'extraTVCommercial', priceKey: 'extraTVCommercialPrice' },
                { nameKey: 'extraLanguage', priceKey: 'extraLanguagePrice' },
                { nameKey: 'extraDomain', priceKey: 'extraDomainPrice' },
                { nameKey: 'extraEcommerce', priceKey: 'extraEcommercePrice' },
                { nameKey: 'extraTestimonials', priceKey: 'extraTestimonialsPrice' },
                { nameKey: 'extraPhotography', priceKey: 'extraPhotographyPrice' },
                { nameKey: 'extraSearch', priceKey: 'extraSearchPrice' },
                { nameKey: 'extraAnalytics', priceKey: 'extraAnalyticsPrice' },
                { nameKey: 'extraAIGeneration', priceKey: 'extraAIGenerationPrice' }
              ].map((extra, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-4 flex justify-between items-center hover:shadow-md transition-shadow duration-300"
                >
                  <span className="font-montserrat text-sm text-[#0b004c]">
                    {t(extra.nameKey)}
                  </span>
                  <span className="font-montserrat-bold text-sm text-[#6a6a6a] text-right whitespace-nowrap ml-4">
                    {t(extra.priceKey)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-[#4a4a4a] rounded-lg p-8 md:p-12 mt-8 text-center">
            <h2 className="font-montserrat-bold text-2xl md:text-3xl text-white mb-6">
              {t('webCtaTitle')}
            </h2>
            <a
              href="/contact#booking-form"
              className="inline-block px-8 py-4 bg-white text-[#4a4a4a] font-montserrat-bold rounded-lg 
                         hover:bg-[#0b004c] hover:text-white hover:scale-105 
                         transition-all duration-300 ease-in-out shadow-lg hover:shadow-xl"
              data-testid="web-cta-btn"
            >
              {t('webCtaButton')}
            </a>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-[#0b004c] py-16">
        <div className="container mx-auto px-4">
          <h2 className="font-montserrat-bold text-2xl md:text-3xl text-white text-center mb-12">
            {t('webDevProcess')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: "01", titleKey: "planning", descKey: "planningDesc" },
              { step: "02", titleKey: "designStep", descKey: "designStepDesc" },
              { step: "03", titleKey: "development", descKey: "developmentDesc" },
              { step: "04", titleKey: "testing", descKey: "testingDesc" },
              { step: "05", titleKey: "launch", descKey: "launchDesc" }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 border-2 border-white rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-montserrat-bold">{item.step}</span>
                </div>
                <h3 className="font-montserrat-bold text-white mb-1">{t(item.titleKey)}</h3>
                <p className="font-montserrat text-white/70 text-sm">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebServicesPage;
